import { BusinessProfileReadinessV1Schema, DEFAULT_BUSINESS_PROFILE_READINESS_POLICY, } from "../contracts/business-profile-readiness.js";
import { ROADMAP_BLOCKED_SITE_PACKAGE_IDS, TIER2_SITE_PACKAGE_IDS, } from "../contracts/site-package.js";
import { EligibilityBlockedResponseSchema } from "../contracts/business-profile-readiness.js";
import { BusinessGraphContradictionInputError, detectBusinessGraphContradictionsFromGraph, invalidInputContradictionResult, } from "../contradiction/detect-business-graph-contradictions.js";
import { packageRequiredFactHardBlockers } from "../site-package/business-graph-package-requirements.js";
function hasContactMethod(graph) {
    return Boolean(graph.business.primaryPhone?.trim() || graph.business.primaryEmail?.trim());
}
function hasServiceArea(graph) {
    return graph.operations.serviceAreas.some((a) => a.name.trim().length > 0);
}
function hasPrimaryService(graph) {
    return graph.services.some((s) => s.name.trim().length > 0);
}
function hasServiceDescriptions(graph) {
    return graph.services.length >= 1;
}
function hasProofEvidence(graph) {
    const trust = graph.trust;
    return (trust.license.length > 0 ||
        trust.insurance.length > 0 ||
        trust.certifications.length > 0 ||
        graph.evidence.records.length > 0 ||
        graph.assets.length > 0);
}
function hasUnsupportedLicensedClaim(graph) {
    const claims = graph.trust.claims;
    const hasLicenseProof = graph.trust.license.length > 0 || graph.trust.certifications.length > 0;
    const hasInsuranceProof = graph.trust.insurance.length > 0;
    for (const claim of claims) {
        const id = claim.id.toLowerCase();
        if ((id.includes("licensed") || id.includes("certified")) &&
            !hasLicenseProof) {
            return true;
        }
        if (id.includes("insured") && !hasInsuranceProof) {
            return true;
        }
    }
    return false;
}
function inferBusinessType(graph) {
    const name = graph.business.businessName.toLowerCase();
    const services = graph.services.map((s) => s.name.toLowerCase()).join(" ");
    const haystack = `${name} ${services}`;
    if (haystack.includes("hvac"))
        return "hvac";
    if (haystack.includes("plumb"))
        return "plumbing";
    if (haystack.includes("landscap"))
        return "landscaping";
    if (haystack.includes("contractor"))
        return "general_contractor";
    if (haystack.includes("restaurant") || haystack.includes("cafe"))
        return "restaurant_or_cafe";
    if (haystack.includes("retail") || haystack.includes("showroom"))
        return "retail_or_showroom";
    if (haystack.includes("medical") || haystack.includes("wellness"))
        return "medical_or_wellness";
    if (haystack.includes("professional"))
        return "professional_service";
    if (haystack.includes("contract"))
        return "contractor";
    return "other";
}
function inferPrimaryEvidenceType(graph) {
    if (graph.trust.license.length > 0 || graph.trust.certifications.length > 0) {
        return "license_or_certification";
    }
    if (graph.assets.some((a) => a.mediaSlot === "beforeAfterImage")) {
        return "before_after_photos";
    }
    if (graph.assets.length > 0) {
        return "project_portfolio";
    }
    if (graph.operations.serviceAreas.length > 0) {
        return "service_area_coverage";
    }
    if (graph.content.offers.length > 0) {
        return "menu_or_offers";
    }
    return "none_yet";
}
function packagesForLevel(level) {
    switch (level) {
        case "NEEDS_BASICS":
            return [];
        case "ONE_PAGE_AVAILABLE":
            return ["package-one-page"];
        case "THREE_PAGE_AVAILABLE":
            return ["package-one-page", "package-basic-3"];
        case "FIVE_PAGE_AVAILABLE":
            return ["package-one-page", "package-basic-3", "package-standard-5", "package-growth-8"];
        case "STRONG_PROFILE_LARGER_LATER":
            return [
                "package-one-page",
                "package-basic-3",
                "package-standard-5",
                "package-growth-8",
                "package-authority-10",
                "package-authority-15",
            ];
    }
}
function isReadinessPolicy(value) {
    return (typeof value === "object" &&
        value !== null &&
        "schemaVersion" in value &&
        value.schemaVersion ===
            "business-profile-readiness-policy.v1.0");
}
export function evaluateBusinessProfileReadiness(graph, policyOrOptions = DEFAULT_BUSINESS_PROFILE_READINESS_POLICY) {
    const options = isReadinessPolicy(policyOrOptions)
        ? { policy: policyOrOptions }
        : policyOrOptions;
    const policy = options.policy ?? DEFAULT_BUSINESS_PROFILE_READINESS_POLICY;
    const missingRequirements = [];
    const warnings = [];
    const hardBlockers = [];
    // Observation timestamp at readiness boundary — detector core never reads wall clock.
    const contradictionCheckedAt = options.contradictionCheckedAt ?? new Date().toISOString();
    let contradictionResult;
    try {
        contradictionResult = detectBusinessGraphContradictionsFromGraph({
            graph,
            state: "CANDIDATE",
            additionalFacts: options.additionalComparableFacts,
            checkedAt: contradictionCheckedAt,
        });
    }
    catch (err) {
        if (err instanceof BusinessGraphContradictionInputError) {
            // Fail closed: mixed/foreign/missing tenant or unknown fieldPath → INVALID_INPUT.
            contradictionResult = invalidInputContradictionResult({
                agencyId: graph.agencyId,
                projectId: graph.projectId,
                revisionId: graph.revisionId,
                evaluatedFromBusinessGraphSnapshotHash: graph.snapshotHash,
                checkedAt: contradictionCheckedAt,
            }, err);
            // Distinguish contradictory truth (Contradiction: CODE) from invalid input.
            hardBlockers.push(`Contradiction input invalid: ${err.code}`);
        }
        else {
            throw err;
        }
    }
    if (!graph.business.businessName.trim()) {
        hardBlockers.push("Missing business name");
    }
    if (policy.requirePrimaryService && !hasPrimaryService(graph)) {
        missingRequirements.push("Add a primary service");
    }
    if (policy.requireContactMethod && !hasContactMethod(graph)) {
        missingRequirements.push("Add a contact method (phone or email)");
    }
    if (policy.requireServiceArea && !hasServiceArea(graph)) {
        missingRequirements.push("Add a service area or operating location");
    }
    if (!hasServiceDescriptions(graph)) {
        missingRequirements.push("Add at least one confirmed service description");
    }
    if (hasUnsupportedLicensedClaim(graph)) {
        hardBlockers.push("Unsupported licensed/insured/certified claim without proof");
    }
    for (const cx of contradictionResult.contradictions) {
        if (cx.blocking) {
            hardBlockers.push(`Contradiction: ${cx.code} — ${cx.message}`);
        }
        else {
            warnings.push(`Contradiction warning: ${cx.code} — ${cx.message}`);
        }
    }
    if (options.forPackage) {
        hardBlockers.push(...packageRequiredFactHardBlockers(graph, options.forPackage));
    }
    if (!hasProofEvidence(graph)) {
        warnings.push("Business profile has limited media or proof evidence");
    }
    if (graph.operations.hours.length === 0) {
        warnings.push("Business hours not provided");
    }
    let readinessLevel = "NEEDS_BASICS";
    if (hardBlockers.length === 0 && missingRequirements.length === 0) {
        const serviceCount = graph.services.length;
        if (serviceCount >= policy.minServicesForFivePage && hasProofEvidence(graph) && hasServiceArea(graph)) {
            readinessLevel =
                serviceCount >= policy.minServicesForFivePage + 1
                    ? "STRONG_PROFILE_LARGER_LATER"
                    : "FIVE_PAGE_AVAILABLE";
        }
        else if (serviceCount >= policy.minServicesForThreePage && hasContactMethod(graph)) {
            readinessLevel = "THREE_PAGE_AVAILABLE";
        }
        else if (hasContactMethod(graph) && hasPrimaryService(graph)) {
            readinessLevel = "ONE_PAGE_AVAILABLE";
        }
    }
    else if (hardBlockers.length === 0 &&
        hasContactMethod(graph) &&
        hasPrimaryService(graph) &&
        graph.business.businessName.trim()) {
        readinessLevel = "ONE_PAGE_AVAILABLE";
        if (!hasServiceArea(graph)) {
            missingRequirements.push("Add a service area or operating location");
        }
    }
    const availablePackages = packagesForLevel(readinessLevel);
    const blockedPackages = [...ROADMAP_BLOCKED_SITE_PACKAGE_IDS];
    for (const pkg of [
        "package-one-page",
        "package-basic-3",
        "package-standard-5",
        ...TIER2_SITE_PACKAGE_IDS,
    ]) {
        if (!availablePackages.includes(pkg)) {
            blockedPackages.push(pkg);
        }
    }
    const evaluatedAt = new Date().toISOString();
    return BusinessProfileReadinessV1Schema.parse({
        schemaVersion: "business-profile-readiness.v1.0",
        readinessLevel,
        availablePackages,
        blockedPackages: [...new Set(blockedPackages)],
        missingRequirements,
        warnings,
        hardBlockers,
        policyVersion: policy.policyVersion,
        evaluatedAt,
        evaluatedFromBusinessGraphSnapshotHash: graph.snapshotHash,
        confirmedFieldsOnly: true,
        businessType: inferBusinessType(graph),
        primaryEvidenceType: inferPrimaryEvidenceType(graph),
        contradictionResult,
    });
}
export function isSitePackageEligible(readiness, requestedPackage, graph) {
    if (ROADMAP_BLOCKED_SITE_PACKAGE_IDS.includes(requestedPackage)) {
        return false;
    }
    if (graph && packageRequiredFactHardBlockers(graph, requestedPackage).length > 0) {
        return false;
    }
    if (TIER2_SITE_PACKAGE_IDS.includes(requestedPackage)) {
        return readiness.availablePackages.includes(requestedPackage);
    }
    return readiness.availablePackages.includes(requestedPackage);
}
export function buildEligibilityBlockedResponse(readiness, requestedPackage) {
    const blockedPackages = readiness.availablePackages.includes(requestedPackage)
        ? [...readiness.blockedPackages]
        : [...new Set([...readiness.blockedPackages, requestedPackage])];
    return EligibilityBlockedResponseSchema.parse({
        status: "ELIGIBILITY_BLOCKED",
        requestedPackage,
        currentReadinessLevel: readiness.readinessLevel,
        availablePackages: readiness.availablePackages,
        blockedPackages,
        missingRequirements: readiness.missingRequirements,
        hardBlockers: readiness.hardBlockers,
        warnings: readiness.warnings,
        policyVersion: readiness.policyVersion,
        contradictionResult: readiness.contradictionResult,
    });
}
//# sourceMappingURL=evaluate-business-profile-readiness.js.map